import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Calendar, MapPin, CheckCircle, Star, Heart, ArrowRight, ArrowLeft, Quote, Users, Briefcase, GraduationCap } from 'lucide-react';
import { Link } from 'react-router-dom';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import { cn } from '@/lib/utils';
import LeergangOnderstroomDialog from '@/components/LeergangOnderstroomDialog';
import LogoSystemique03 from '@/assets/logos/Logo Systémique-03.webp';
import voorwaardenPdf from '@/assets/Algemene-voorwaarden-Systemique.pdf';
import OptimizedImage from '@/components/OptimizedImage';
import { SEO } from '@/components/SEO';
import leergangVisual from '@/assets/webp bestanden/leergang-systemisch-leiderschap-marthe.webp?w=1200&format=webp';
import leergangVisualSrcset from '@/assets/webp bestanden/leergang-systemisch-leiderschap-marthe.webp?w=480;800;1200&format=webp&as=srcset';
import leergangVisualUniek from '@/assets/webp bestanden/leergang-systemisch-leiderschap-opstelling.webp?w=900&format=webp';
import leergangVisualUniekSrcset from '@/assets/webp bestanden/leergang-systemisch-leiderschap-opstelling.webp?w=400;700;900&format=webp&as=srcset';

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl border border-transparent hover:border-[#854C73]/10 h-full",
      className
    )}
    {...props}
  >
    <div className="relative h-full flex flex-col">{children}</div>
  </div>
);

// Data structuur voor leergang edities
interface LeergangEditie {
  id: string;
  seizoen: string;
  jaar: number;
  startDate: string | null;
  endDate: string | null;
  startMaand: string | null; // Voor weergave als exacte datum nog niet bekend is
  specifiekeData?: string[]; // Specifieke data voor weergave in accordion (bijv. ["29 oktober 2026", "30 oktober 2026", "27 november 2026"])
  location: {
    name: string | null;
    url: string | null;
    address: {
      streetAddress: string | null;
      addressLocality: string | null;
      postalCode: string | null;
    } | null;
  } | null;
  status: 'upcoming' | 'active' | 'completed';
}

const seoFaqs = [
  {
    question: 'Is dit een opleiding of een leergang systemisch leiderschap?',
    answer:
      'Dit is een leergang: compact (3 dagen), praktisch en gericht op systemisch leiderschap in je dagelijkse rol. Geen jarenlange opleiding, wel inzichten en toepasbare handelingsperspectieven.',
  },
  {
    question: 'Wat is het verschil tussen systemisch werk en systemisch leiderschap?',
    answer:
      'Systemisch werk is de methodiek (opstellingen, onderstroom, systeemdynamieken). Systemisch leiderschap is hoe jij die bril gebruikt als leider of adviseur: scherper waar nemen, interveniëren en richting geven in teams en organisaties.',
  },
  {
    question: 'Waar vindt Duiken in de Onderstroom plaats?',
    answer:
      'De leergang vindt plaats in de regio Amsterdam en Noord-Holland. De editie najaar 2026 is in Stadsoase naar Zee, Amsterdam (IJburg). Vanuit Haarlem, Castricum en omgeving goed bereikbaar.',
  },
  {
    question: 'Voor wie is deze leergang systemisch leiderschap bedoeld?',
    answer:
      'Voor leiders, managers, directeuren, projectleiders en adviseurs die pragmatisch systemisch willen leren kijken en handelen en die de onderstroom willen herkennen in plaats van alleen symptomen te behandelen.',
  },
];

const leergangEdities: LeergangEditie[] = [
  {
    id: 'najaar-2026',
    seizoen: 'Najaar',
    jaar: 2026,
    startDate: "2026-10-29",
    endDate: "2026-11-27",
    startMaand: null,
    specifiekeData: ["29 en 30 oktober 2026", "27 november 2026"],
    location: {
      name: "Stadsoase naar Zee",
      url: "https://www.stadsoasenaarzee.com",
      address: {
        streetAddress: "Bert Haanstrakade 102",
        addressLocality: "Amsterdam",
        postalCode: "1087 DN"
      }
    },
    status: 'active'
  }
];

const LeergangOnderstroom = () => {
  const [leergangDialogOpen, setLeergangDialogOpen] = useState(false);
  
  // Filter: alleen upcoming/active edities
  const activeEdities = leergangEdities.filter(e => 
    e.status === 'upcoming' || e.status === 'active'
  );
  
  // Helper functie voor Course schema generatie
  const generateCourseSchema = (editie: LeergangEditie) => {
    const courseTitle = `Leergang systemisch leiderschap: Duiken in de Onderstroom - ${editie.seizoen} ${editie.jaar}`;
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": courseTitle,
      "courseName": courseTitle,
      "description": "Leergang systemisch leiderschap voor leiders en adviseurs. Leer de onderstroom herkennen en daar concreet mee werken in 3 dagen praktijkgericht systemisch leiderschap.",
      "provider": {
        "@type": "Organization",
        "name": "Systemique",
        "url": "https://systemique.nl",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Castricum",
          "addressRegion": "Noord-Holland",
          "addressCountry": "NL"
        },
        "telephone": "+31-6-12991453",
        "email": "marthe@systemique.nl"
      },
      "courseCode": `LEERGANG-ONDERSTROOM-${editie.seizoen.toUpperCase()}-${editie.jaar}`,
      "educationalLevel": "Professional",
      "teaches": [
        "Systemisch werk",
        "Organisatieopstellingen",
        "Systemische interventies",
        "Onderstroom in organisaties",
        "Systemisch leiderschap"
      ],
      "timeRequired": "P3D",
      "inLanguage": "nl",
      "audience": {
        "@type": "Audience",
        "audienceType": "Leiders, managers, adviseurs en professionals"
      },
      "coursePrerequisites": "Ervaring in leiderschap of advisering",
      "offers": {
        "@type": "Offer",
        "price": "1500",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock",
        "url": "https://systemique.nl/leergang-systemisch-leiderschap"
      },
      "url": "https://systemique.nl/leergang-systemisch-leiderschap",
      "instructor": {
        "@type": "Person",
        "@id": "https://systemique.nl/over#marthe",
        "name": "Marthe van der Molen",
        "jobTitle": "Systemisch organisatiecoach en trainer",
        "worksFor": {
          "@type": "Organization",
          "name": "Systemique",
          "url": "https://systemique.nl"
        },
        "url": "https://systemique.nl/over",
        "sameAs": [
          "https://www.linkedin.com/in/marthe-van-der-molen"
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "9",
        "bestRating": "10",
        "worstRating": "1",
        "ratingCount": "4",
        "reviewCount": "4"
      },
      "review": [
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "9",
            "bestRating": "10",
            "worstRating": "1"
          },
          "author": {
            "@type": "Person",
            "name": "Deelnemer Leergang Duiken in de Onderstroom"
          },
          "reviewBody": "De leergang heeft mij meer bewustzijn, taal en lef gegeven om te kijken naar en te werken met de onderstroom. Ik ben mij bewuster van mijn eigen rol, zie scherper wat er speelt binnen de organisatie en durf dit ook vaker bespreekbaar te maken."
        },
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "9",
            "bestRating": "10",
            "worstRating": "1"
          },
          "author": {
            "@type": "Person",
            "name": "Deelnemer Leergang Duiken in de Onderstroom"
          },
          "reviewBody": "De bewustwording en bril van systemisch kijken heeft veel in beweging gezet. Op persoonlijk vlak over mijn eigen positie. Vooral welk patroon ik nu doorbreek en wat ik nodig heb (duidelijke plek)."
        },
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "9",
            "bestRating": "10",
            "worstRating": "1"
          },
          "author": {
            "@type": "Person",
            "name": "Deelnemer Leergang Duiken in de Onderstroom"
          },
          "reviewBody": "Enorm veel inzichten op systemisch vlak voor mijn team en persoonlijk."
        }
      ]
    };

    // Voeg locatie toe als beschikbaar
    if (editie.location) {
      (baseSchema as any).location = {
        "@type": "Place",
        "name": editie.location.name || "Locatie volgt",
        ...(editie.location.url && { "url": editie.location.url }),
        ...(editie.location.address && {
          "address": {
            "@type": "PostalAddress",
            ...(editie.location.address.streetAddress && { "streetAddress": editie.location.address.streetAddress }),
            ...(editie.location.address.addressLocality && { "addressLocality": editie.location.address.addressLocality }),
            ...(editie.location.address.postalCode && { "postalCode": editie.location.address.postalCode }),
            "addressRegion": "Noord-Holland",
            "addressCountry": "NL"
          }
        })
      };
    }

    // Voeg datums toe als beschikbaar
    if (editie.startDate) {
      (baseSchema as any).startDate = editie.startDate;
    }
    if (editie.endDate) {
      (baseSchema as any).endDate = editie.endDate;
    }
    if (editie.startDate || editie.endDate) {
      (baseSchema as any).eventStatus = "https://schema.org/EventScheduled";
    }

    return baseSchema;
  };
  
  return (
    <>
      <SEO 
        title="Leergang systemisch leiderschap | Duiken in de Onderstroom - Systemique"
        description="Leergang systemisch leiderschap voor leiders en adviseurs. 3 dagen praktisch systemisch kijken en handelen met Duiken in de Onderstroom. Najaar 2026, Amsterdam & Noord-Holland. €1.500 excl. btw."
        keywords="leergang systemisch leiderschap, leergang systemisch leiderschap Amsterdam, systemisch leiderschap opleiding, leergang onderstroom, Duiken in de Onderstroom, systemische leergang leiders, leergang systemisch werk Noord-Holland, training systemisch leiderschap, leergang najaar 2026"
        canonical="https://systemique.nl/leergang-systemisch-leiderschap"
        ogTitle="Leergang systemisch leiderschap | Duiken in de Onderstroom"
        ogDescription="Compacte leergang (3 dagen) voor leiders en adviseurs. Leer systemisch leiderschap in de praktijk — najaar 2026."
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: seoFaqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      {/* Person schema voor docent — E-E-A-T signal */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            '@id': 'https://systemique.nl/over#marthe',
            name: 'Marthe van der Molen',
            jobTitle: 'Systemisch organisatiecoach en trainer',
            description:
              'Marthe van der Molen begeleidt al ruim tien jaar leiders, adviseurs, teams en organisaties in systemisch werk en organisatieontwikkeling. Zij is de docent van de leergang systemisch leiderschap "Duiken in de Onderstroom".',
            url: 'https://systemique.nl/over',
            image: 'https://systemique.nl/OG-image.jpg',
            worksFor: {
              '@type': 'Organization',
              '@id': 'https://systemique.nl/#organization',
              name: 'Systemique',
              url: 'https://systemique.nl',
            },
            knowsAbout: [
              'Systemisch werk',
              'Systemisch leiderschap',
              'Organisatieopstellingen',
              'Familieopstellingen',
              'Organisatieontwikkeling',
              'Teamcoaching',
            ],
            sameAs: [
              'https://www.linkedin.com/in/marthe-van-der-molen',
              'https://www.instagram.com/systemique.nl',
            ],
          }),
        }}
      />

      {/* BreadcrumbList — helpt sitelinks en navigatiestructuur */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://systemique.nl/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Opleidingstrajecten',
                item: 'https://systemique.nl/opleidingstrajecten',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Leergang systemisch leiderschap',
                item: 'https://systemique.nl/leergang-systemisch-leiderschap',
              },
            ],
          }),
        }}
      />
      
      {/* Course Schemas - Dynamisch genereren voor actieve edities */}
      {activeEdities.map((editie) => (
        <script
          key={`course-schema-${editie.id}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateCourseSchema(editie))
          }}
        />
      ))}
      
      {/* Hero Section */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>

          <p className="text-sm uppercase tracking-widest text-[#F9EBEF]/70 mb-4">
            Systemique · Amsterdam &amp; Noord-Holland
          </p>
          
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl text-[#F9EBEF] mb-4 font-bold leading-tight">
            Leergang systemisch leiderschap
          </h1>

          <p className="font-serif text-2xl md:text-3xl italic font-light text-[#F9EBEF]/90 mb-6">
            Duiken in de Onderstroom
          </p>
          
          <p className="text-lg md:text-xl text-[#F9EBEF]/85 font-light max-w-2xl mx-auto mb-8 leading-relaxed">
            Compacte leergang (3 dagen) voor leiders en adviseurs die de onderstroom willen herkennen
            en daar concreet mee willen werken in hun rol.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {['3 dagen', 'Max. 8 deelnemers', 'Najaar 2026', '€ 1.500 excl. btw'].map((label) => (
              <span
                key={label}
                className="px-4 py-1.5 rounded-full border border-[#F9EBEF]/30 text-sm text-[#F9EBEF]/90"
              >
                {label}
              </span>
            ))}
          </div>
          
          <button
            onClick={() => setLeergangDialogOpen(true)}
            className="luxe-button -mt-2"
          >
            Meld je aan voor de leergang
          </button>
        </div>
      </section>

      {/* Info Bar - Beide Edities */}
      <section className="py-12 px-6 relative z-30">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6">
            {activeEdities.map((editie) => (
              <div key={editie.id} className="bg-white rounded-2xl shadow-xl p-8">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <Badge className="bg-[#FAEBEE] text-[#854C73] border-0 px-4 py-1.5 text-sm font-bold uppercase tracking-wide">
                    {editie.seizoen} {editie.jaar}
                  </Badge>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] shrink-0">
              <Calendar size={24} />
            </div>
            <div className="text-left">
                <p className="text-xs text-[#854C73] uppercase tracking-wider font-bold mb-1">Startdatum</p>
                      <p className="text-[#6b4912] text-xl font-serif">
                        {editie.startDate 
                          ? new Date(editie.startDate).toLocaleDateString('nl-NL', { 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric' 
                            })
                          : editie.startMaand 
                            ? <>{editie.startMaand} {editie.jaar} <span className="text-base">(exacte datum volgt)</span></>
                            : 'Datum volgt'}
                      </p>
            </div>
          </div>
                  
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] shrink-0">
              <MapPin size={24} />
            </div>
            <div className="text-left">
                <p className="text-xs text-[#854C73] uppercase tracking-wider font-bold mb-1">Locatie</p>
                      {editie.location && editie.location.name ? (
                        <a 
                          href={editie.location.url || "#"} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#6b4912] hover:text-[#854C73] text-xl font-serif transition-colors underline decoration-dotted"
                        >
                          {editie.location.name}, Amsterdam
                        </a>
                      ) : (
                        <p className="text-[#6b4912] text-xl font-serif">Locatie volgt</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wat is systemisch leiderschap + aanmelden */}
      <section className="py-16 md:py-24 px-6 relative bg-white">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-7 space-y-8">
              <div className="space-y-6">
                <h2
                  className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#854C73] mb-6 leading-tight"
                  style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
                >
                  Wat is een leergang systemisch leiderschap?
                </h2>
                <div className="space-y-6 text-[#6b4912] text-lg leading-relaxed">
                  <p>
                    <strong className="text-[#854C73]">Systemisch leiderschap</strong> betekent: niet alleen kijken naar wat zichtbaar is
                    (gedrag, procedures, conflicten), maar ook naar de <em>onderstroom</em>, patronen,
                    loyaliteiten en dynamieken die teams en organisaties sturen.
                  </p>
                  <p>
                    In deze leergang leer je die bril <strong>praktisch</strong> toepassen: in gesprekken,
                    in casuïstiek en via systemische interventies. Geen theoretische opleiding van maanden,
                    maar een compact traject voor ervaren leiders en adviseurs.
                  </p>
                  <p>
                    Het programma heet{' '}
                    <span className="italic font-serif text-[#854C73]">Duiken in de Onderstroom</span>
                    {' '}, een leergang systemisch leiderschap ontwikkeld door Marthe van der Molen (Systemique).
                  </p>
                </div>
              </div>

              <div className="group rounded-3xl overflow-hidden shadow-lg max-w-md">
                <div className="aspect-[4/3] overflow-hidden">
                  <OptimizedImage
                    src={leergangVisual}
                    srcSet={leergangVisualSrcset}
                    sizes="(max-width: 448px) 100vw, 448px"
                    alt="Marthe van der Molen tijdens een leergang systemisch leiderschap — Duiken in de Onderstroom bij Systemique"
                    className="w-full h-full object-cover object-[50%_45%] transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                    width={1200}
                    height={900}
                  />
                </div>
              </div>
            </div>

            <aside className="md:col-span-5 md:sticky md:top-24 space-y-6">
              {activeEdities.map((editie) => (
                <SpotlightCard key={editie.id} className="p-8">
                  <Badge className="mb-4 bg-[#FAEBEE] text-[#854C73] border-0 px-4 py-1.5 text-sm font-bold uppercase tracking-wide">
                    Volgende editie · {editie.seizoen} {editie.jaar}
                  </Badge>
                  <h3
                    className="font-serif text-2xl text-[#854C73] mb-4"
                    style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
                  >
                    Leergang Duiken in de Onderstroom
                  </h3>
                  <ul className="space-y-3 text-[#6b4912] mb-6">
                    <li className="flex items-start gap-3">
                      <Calendar size={18} className="text-[#854C73] mt-0.5 shrink-0" />
                      <span>
                        {editie.specifiekeData?.join(' · ') ||
                          (editie.startDate
                            ? new Date(editie.startDate).toLocaleDateString('nl-NL', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })
                            : 'Data volgen')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin size={18} className="text-[#854C73] mt-0.5 shrink-0" />
                      <span>
                        {editie.location?.name
                          ? `${editie.location.name}, Amsterdam`
                          : 'Locatie volgt'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users size={18} className="text-[#854C73] mt-0.5 shrink-0" />
                      <span>Max. 8 deelnemers</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-[#854C73] mt-0.5 shrink-0" />
                      <span>€ 1.500 excl. btw · intake &amp; eindcoaching inbegrepen</span>
                    </li>
                  </ul>
                  <Button
                    className="w-full bg-[#854C73] hover:bg-[#590D0D] text-white rounded-xl py-6 font-serif"
                    onClick={() => setLeergangDialogOpen(true)}
                  >
                    Aanmelden
                  </Button>
                  <p className="text-xs text-center text-[#6b4912]/70 mt-4">
                    Intake vóór start · kleine groep
                  </p>
                </SpotlightCard>
              ))}

              <SpotlightCard className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Star size={28} className="text-[#854C73]" strokeWidth={1.5} />
                  <h3
                    className="font-serif text-2xl text-[#854C73]"
                    style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 500 }}
                  >
                    Wat je leert
                  </h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Belangrijkste systeemdynamieken herkennen',
                    'Je eigen systemische patronen doorgronden',
                    'Praktisch handelingsperspectief voor casuïstiek',
                    'Eigen organisatieopstelling met vaak diepe inzichten',
                  ].map((goal, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={22} className="text-[#854C73] mt-0.5 shrink-0" />
                      <span className="text-[#6b4912] leading-relaxed">{goal}</span>
                    </li>
                  ))}
                </ul>
              </SpotlightCard>
            </aside>
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-16 md:py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <h2
              className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4"
              style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
            >
              Voor wie is deze <span className="italic font-light font-serif">leergang systemisch leiderschap</span>?
            </h2>
            <p className="text-[#6b4912] text-lg md:text-xl font-light max-w-2xl mx-auto">
              Leiders, managers en adviseurs die systemisch willen <em>leiden</em> — niet alleen analyseren.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: 'Leidinggevenden',
                text: 'Je voelt dat er meer speelt dan wat in vergaderingen wordt gezegd — en wilt de onderstroom benoemen.',
              },
              {
                icon: Briefcase,
                title: 'HR & adviseurs',
                text: 'Je begeleidt verandering en wilt scherper zien waar het systeem vastzit en wat beweging vraagt.',
              },
              {
                icon: GraduationCap,
                title: 'Ervaren professionals',
                text: 'Je kent opstellingen of systemisch werk en wilt het verankeren in je dagelijkse leiderschapspraktijk.',
              },
            ].map(({ icon: Icon, title, text }) => (
              <SpotlightCard key={title} className="p-8 text-center">
                <div className="w-14 h-14 mx-auto bg-[#FAEBEE] text-[#854C73] rounded-full flex items-center justify-center mb-5">
                  <Icon size={24} strokeWidth={1.5} />
                </div>
                <h3
                  className="font-serif text-xl md:text-2xl text-[#854C73] mb-3"
                  style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 500 }}
                >
                  {title}
                </h3>
                <p className="text-[#6b4912] leading-relaxed font-light">{text}</p>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </section>

      {/* Het Programma (Modules) */}
      <section className="py-12 md:py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <GeoPattern />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 
              className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4"
              style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
            >
              Programma: <span className="italic font-light font-serif">3 dagen systemisch leiderschap</span>
            </h2>
            <p className="text-[#6b4912] text-xl font-light">Theorie, ervaren, oefenen en casuïstiek — Duiken in de Onderstroom.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Module 1 (2 dagen)",
                subtitle: "Duiken in de onderstroom",
                description: "Tijdens deze module ligt de focus op het doorgronden van de belangrijkste systemische wetmatigheden, dynamieken en patronen. Dit doen we aan de hand van theorie en de werkpraktijk van de deelnemers."
              },
              {
                title: "Module 2 (1 dag)",
                subtitle: "Waarnemen en interveniëren",
                description: "Alle deelnemers werken aan een real life case. Je leert systemisch waar te nemen in gesprekken en interventies te doen. We werken met verschillende real life cases en onderzoeken welke interventies effect hebben."
              }
            ].map((mod, idx) => (
              <SpotlightCard key={idx} className="p-10">
                <div className="mb-6">
                  <Badge className="bg-[#FAEBEE] text-[#854C73] border-0 px-4 py-1.5 text-sm font-bold uppercase tracking-wide">
                        {mod.title}
                  </Badge>
                </div>
                <h3 
                  className="font-serif text-3xl text-[#854C73] mb-4"
                  style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
                >
                    {mod.subtitle === "Duiken in de onderstroom" 
                      ? <>Duiken in de <span className="italic font-light font-serif">onderstroom</span></>
                      : mod.subtitle === "Waarnemen en interveniëren"
                      ? <>Waarnemen en <span className="italic font-light font-serif">interveniëren</span></>
                      : mod.subtitle}
                </h3>
                <p className="text-[#6b4912] text-lg leading-relaxed flex-grow">
                    {mod.description}
                </p>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </section>

      {/* Unieke Kenmerken (USP's) */}
      <section className="py-24 px-6 relative bg-white">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <h2 
              className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4"
              style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
            >
              Wat maakt deze leergang <span className="italic font-light font-serif">uniek</span>?
            </h2>
         </div>

         <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
           {/* Foto links — opstelling-in-actie */}
           <div className="md:col-span-5">
             <div className="group rounded-3xl overflow-hidden shadow-lg">
               <div className="aspect-[3/4] overflow-hidden">
                 <OptimizedImage
                   src={leergangVisualUniek}
                   srcSet={leergangVisualUniekSrcset}
                   sizes="(max-width: 768px) 100vw, 480px"
                   alt="Deelnemers tijdens een organisatieopstelling in de leergang systemisch leiderschap bij Systemique"
                   className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                   width={900}
                   height={1200}
                 />
               </div>
             </div>
           </div>

           {/* 2x2 grid met unieke kenmerken */}
           <div className="md:col-span-7 grid sm:grid-cols-2 gap-6 md:gap-8">
            {[
              { title: "Voor leiders & adviseurs", text: "Speciaal voor leiders en adviseurs die systemische bril willen toevoegen." },
              { title: "Eigen organisatieopstelling", text: "Je krijgt je eigen organisatieopstelling met vaak impactvolle inzichten." },
              { title: "Compact maar krachtig", text: "De opleiding is compact, maar rijk aan stof en inzichten." },
              { title: "Kleine groep", text: "Je leert in een kleine groep (max 8 deelnemers) voor meer diepgang." }
            ].map((item, i) => (
              <div key={i} className="text-center p-6 md:p-8 rounded-2xl hover:bg-[#FAEBEE]/50 transition duration-300">
                <div className="w-14 h-14 mx-auto bg-[#FAEBEE] text-[#854C73] rounded-full flex items-center justify-center mb-5">
                  <Heart size={24} strokeWidth={1.5} fill={i % 2 === 0 ? "currentColor" : "none"} />
                    </div>
                <h4 
                  className="font-serif text-xl md:text-2xl text-[#854C73] mb-3"
                  style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 500 }}
                >
                  {item.title}
                </h4>
                <p className="text-[#6b4912] text-base md:text-lg leading-relaxed font-light">{item.text}</p>
                </div>
            ))}
          </div>
         </div>
         </div>
      </section>

      {/* Reviews / Ervaringen — Bento Layout */}
      <section className="py-16 md:py-24 px-6 relative bg-white overflow-hidden">
        <GrainTexture opacity={0.08} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#FAEBEE] rounded-full blur-3xl opacity-40 -z-0"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          {/* Section Header */}
          <div className="text-center mb-14 md:mb-16">
            <Badge className="mb-4 bg-[#FAEBEE] text-[#854C73] border-0 px-4 py-1.5 text-sm font-bold uppercase tracking-wide">
              Ervaringen
            </Badge>
            <h2
              className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4"
              style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
            >
              Wat <span className="italic font-light font-serif">deelnemers</span> zeggen
            </h2>
            <p className="text-[#6b4912] text-lg md:text-xl font-light max-w-2xl mx-auto">
              Reflecties van leiders en adviseurs die de leergang systemisch leiderschap volgden.
            </p>

            {/* Gemiddelde waardering pill — RatingPill */}
            <div
              className="group/pill inline-flex items-center gap-4 mt-8 px-6 py-3 bg-white rounded-full border border-[#854C73]/10 hover:border-[#854C73]/40 shadow-[0_8px_24px_-8px_rgba(133,76,115,0.25)] hover:shadow-[0_12px_32px_-8px_rgba(133,76,115,0.35)] transition-all duration-300 ease-out"
            >
              <div className="flex gap-0.5" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} className="text-[#854C73]" fill="currentColor" />
                ))}
              </div>
              <div className="h-5 w-px bg-[#854C73]/20" aria-hidden="true"></div>
              <span className="text-[#6b4912] text-sm md:text-base">
                Gemiddeld gewaardeerd met een{' '}
                <span
                  className="font-bold text-[#854C73] text-lg"
                  style={{ fontFamily: "'Noto Serif Display', serif" }}
                >
                  9
                </span>
                <span className="opacity-60"> / 10</span>
              </span>
            </div>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-12 gap-6 md:gap-8">
            {/* Highlight Quote — groot, paars */}
            <div className="md:col-span-7 md:row-span-2 relative rounded-3xl overflow-hidden shadow-2xl bg-[#854C73] text-[#F9EBEF] p-10 md:p-14 flex flex-col">
              <GrainTexture opacity={0.15} />
              <div className="absolute -top-8 -right-8 w-64 h-64 border border-[#F9EBEF]/10 rounded-full opacity-40 pointer-events-none"></div>
              
              <Quote
                className="absolute top-8 right-8 text-[#F9EBEF]/15 rotate-180"
                size={120}
                strokeWidth={1}
                aria-hidden="true"
              />
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex gap-1" aria-label="9 van 10">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={18} className="text-[#F9EBEF]" fill="currentColor" />
                    ))}
                  </div>
                  <span className="text-[#F9EBEF]/80 text-sm font-semibold tracking-wide">
                    <span className="text-[#F9EBEF] font-serif text-base" style={{ fontFamily: "'Noto Serif Display', serif" }}>9</span>
                    <span className="text-[#F9EBEF]/60">/10</span>
                  </span>
                </div>

                <blockquote
                  className="font-serif text-xl md:text-2xl lg:text-[1.75rem] leading-snug italic font-light flex-grow"
                  style={{ fontFamily: "'Noto Serif Display', serif" }}
                >
                  "De leergang heeft mij meer bewustzijn, taal en lef gegeven om te kijken naar en te werken met de onderstroom. Ik ben mij bewuster van mijn eigen rol, zie scherper wat er speelt binnen de organisatie en durf dit ook vaker bespreekbaar te maken."
                </blockquote>

                <figcaption className="mt-10 pt-8 border-t border-[#F9EBEF]/20">
                  <p className="text-[#F9EBEF]/80 text-sm tracking-[0.2em] uppercase font-light">
                    — Deelnemer Leergang Onderstroom
                  </p>
                </figcaption>
              </div>
            </div>

            {/* Smaller Quote 1 */}
            <div className="md:col-span-5 group relative rounded-3xl overflow-hidden bg-[#FAEBEE] p-8 md:p-10 shadow-lg hover:shadow-xl transition-all duration-300 border border-transparent hover:border-[#854C73]/20">
              <Quote
                className="absolute top-6 right-6 text-[#854C73]/15 rotate-180"
                size={64}
                strokeWidth={1}
                aria-hidden="true"
              />
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1" aria-label="9 van 10">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="text-[#854C73]" fill="currentColor" />
                    ))}
                  </div>
                  <span className="text-[#6b4912]/80 text-sm font-semibold tracking-wide">
                    <span className="text-[#854C73] font-serif text-base" style={{ fontFamily: "'Noto Serif Display', serif" }}>9</span>
                    <span className="text-[#6b4912]/50">/10</span>
                  </span>
                </div>

                <blockquote
                  className="font-serif text-lg md:text-xl leading-relaxed italic text-[#6b4912] font-light flex-grow"
                  style={{ fontFamily: "'Noto Serif Display', serif" }}
                >
                  "De bewustwording en bril van systemisch kijken heeft veel in beweging gezet. Op persoonlijk vlak over mijn eigen positie. Vooral welk patroon ik nu doorbreek en wat ik nodig heb (duidelijke plek)."
                </blockquote>

                <figcaption className="mt-6 pt-6 border-t border-[#854C73]/10">
                  <p className="text-[#6b4912]/70 text-xs tracking-[0.2em] uppercase">
                    — Deelnemer Leergang Onderstroom
                  </p>
                </figcaption>
              </div>
            </div>

            {/* Smaller Quote 2 */}
            <div className="md:col-span-5 group relative rounded-3xl overflow-hidden bg-white p-8 md:p-10 shadow-lg hover:shadow-xl transition-all duration-300 border border-[#854C73]/15 hover:border-[#854C73]/30">
              <Quote
                className="absolute top-6 right-6 text-[#854C73]/15 rotate-180"
                size={64}
                strokeWidth={1}
                aria-hidden="true"
              />
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1" aria-label="9 van 10">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="text-[#854C73]" fill="currentColor" />
                    ))}
                  </div>
                  <span className="text-[#6b4912]/80 text-sm font-semibold tracking-wide">
                    <span className="text-[#854C73] font-serif text-base" style={{ fontFamily: "'Noto Serif Display', serif" }}>9</span>
                    <span className="text-[#6b4912]/50">/10</span>
                  </span>
                </div>

                <blockquote
                  className="font-serif text-lg md:text-xl leading-relaxed italic text-[#6b4912] font-light flex-grow"
                  style={{ fontFamily: "'Noto Serif Display', serif" }}
                >
                  "Enorm veel inzichten op systemisch vlak voor mijn team en persoonlijk."
                </blockquote>

                <figcaption className="mt-6 pt-6 border-t border-[#854C73]/10">
                  <p className="text-[#6b4912]/70 text-xs tracking-[0.2em] uppercase">
                    — Deelnemer Leergang Onderstroom
                  </p>
                </figcaption>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Veelgestelde vragen (incl. praktische info) */}
      <section className="py-16 md:py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-10">
            <h2
              className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4"
              style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
            >
              Veelgestelde <span className="italic font-light font-serif">vragen</span>
            </h2>
            <p className="text-[#6b4912] font-light text-lg">
              Over de leergang, inschrijving, data en investering.
            </p>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="px-8 pb-8 pt-0">
              <Accordion type="single" collapsible className="w-full text-left">
                {seoFaqs.map((faq, index) => (
                  <AccordionItem key={faq.question} value={`seo-faq-${index}`}>
                    <AccordionTrigger className="text-left text-lg font-semibold text-[#854C73]">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#6b4912] pt-4 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}

                <AccordionItem value="data-locatie">
                  <AccordionTrigger className="text-left text-lg font-semibold text-[#854C73]">
                    Wanneer en waar vindt de leergang plaats?
                  </AccordionTrigger>
                  <AccordionContent className="text-[#6b4912] pt-4">
                    <div className="space-y-6">
                      {activeEdities.map((editie) => (
                        <div key={editie.id} className="pb-4 border-b border-[#FAEBEE] last:border-0 last:pb-0">
                          <h4 className="font-semibold text-[#854C73] mb-3 text-lg">
                            {editie.seizoen} {editie.jaar}
                          </h4>
                          <ul className="space-y-2">
                            {editie.specifiekeData && editie.specifiekeData.length > 0 ? (
                              editie.specifiekeData.map((datum, idx) => (
                                <li key={idx} className="flex items-center gap-3">
                                  <div className="w-2 h-2 bg-[#854C73] rounded-full"></div>
                                  <span>{datum}</span>
                                </li>
                              ))
                            ) : editie.startDate ? (
                              <li className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-[#854C73] rounded-full"></div>
                                <span>
                                  {new Date(editie.startDate).toLocaleDateString('nl-NL', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                  })}
                                </span>
                              </li>
                            ) : editie.startMaand ? (
                              <li className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-[#854C73] rounded-full"></div>
                                <span>
                                  {editie.startMaand} {editie.jaar}{' '}
                                  <span className="text-sm">(exacte datum volgt)</span>
                                </span>
                              </li>
                            ) : (
                              <li className="flex items-center gap-3">
                                <div className="w-2 h-2 bg-[#854C73] rounded-full"></div>
                                <span>Datum volgt</span>
                              </li>
                            )}
                            {editie.location && editie.location.name ? (
                              <li className="flex items-start gap-3 mt-3">
                                <MapPin size={20} className="text-[#854C73] mt-0.5 shrink-0" />
                                <div>
                                  <p className="font-semibold mb-1">Locatie:</p>
                                  {editie.location.address && (
                                    <>
                                      <p>{editie.location.address.streetAddress}</p>
                                      <p>
                                        {editie.location.address.postalCode}{' '}
                                        {editie.location.address.addressLocality}
                                      </p>
                                    </>
                                  )}
                                  {editie.location.url && (
                                    <a
                                      href={editie.location.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#854C73] hover:underline mt-2 inline-block"
                                    >
                                      www.stadsoasenaarzee.com →
                                    </a>
                                  )}
                                </div>
                              </li>
                            ) : (
                              <li className="flex items-start gap-3 mt-3">
                                <MapPin size={20} className="text-[#854C73] mt-0.5 shrink-0" />
                                <div>
                                  <p className="font-semibold mb-1">Locatie:</p>
                                  <p>Locatie volgt</p>
                                </div>
                              </li>
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="docenten">
                  <AccordionTrigger className="text-left text-lg font-semibold text-[#854C73]">
                    Wie begeleidt de leergang?
                  </AccordionTrigger>
                  <AccordionContent className="text-[#6b4912] pt-4 leading-relaxed">
                    <p>
                      De leergang wordt begeleid door <strong>Marthe van der Molen</strong>, systemisch
                      organisatiecoach en trainer met ruime ervaring in het begeleiden van leiders,
                      adviseurs, teams en organisaties. Ze werkt op het snijvlak van praktische
                      verandervraagstukken, organisatieontwikkeling en systemische onderstromen. Haar
                      stijl is helder, warm en confronterend als het nodig is.
                    </p>

                    <figure className="relative mt-6 bg-[#FAEBEE]/60 border-l-2 border-[#854C73] rounded-r-xl px-6 py-5">
                      <Quote
                        className="absolute -top-2 -left-2 text-[#854C73]/20 rotate-180"
                        size={32}
                        strokeWidth={1}
                        aria-hidden="true"
                      />
                      <blockquote
                        className="font-serif text-lg italic text-[#6b4912] leading-relaxed"
                        style={{ fontFamily: "'Noto Serif Display', serif" }}
                      >
                        "Scherp, warm en veilig tegelijk met veel kundigheid en concreet, zonder dat het
                        zweverig wordt."
                      </blockquote>
                      <figcaption className="mt-3 text-xs tracking-[0.2em] uppercase text-[#854C73]/80">
                        — Deelnemer over Marthe
                      </figcaption>
                    </figure>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="intake-coaching">
                  <AccordionTrigger className="text-left text-lg font-semibold text-[#854C73]">
                    Hoe werkt intake en eindcoaching?
                  </AccordionTrigger>
                  <AccordionContent className="text-[#6b4912] pt-4 leading-relaxed">
                    Bij aanmelding voor de leergang vindt er een intake plaats waarin we samen jouw
                    ontwikkelbehoefte voor de leergang verkennen. Twee maanden na afloop heb je een
                    coachgesprek om te reflecteren op de inzichten vanuit de training en waar nodig een
                    verdiepingsslag te maken op een thema dat zich aandient.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="prijs">
                  <AccordionTrigger className="text-left text-lg font-semibold text-[#854C73]">
                    Wat kost de leergang?
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="flex justify-center mb-6">
                      <Card
                        onClick={() => setLeergangDialogOpen(true)}
                        className="w-full max-w-lg cursor-pointer border-2 border-[#854C73] bg-gradient-to-br from-[#854C73] to-[#854C73] text-white transition duration-200 hover:scale-[1.01] hover:shadow-xl"
                      >
                        <CardContent className="p-8 text-center space-y-3">
                          <div className="text-sm uppercase tracking-[0.15em] font-serif font-semibold text-[#F9EBEF]">
                            Investering
                          </div>
                          <div className="text-4xl font-bold">€1.500</div>
                          <p className="text-white/80 text-xs">excl. btw</p>
                          <div className="space-y-2 text-sm text-white/90 max-w-xs mx-auto text-left">
                            {[
                              '3-daagse leergang',
                              'Inclusief intake & eindcoaching',
                              'Kleine groep, max. 8 deelnemers',
                            ].map((item) => (
                              <div key={item} className="flex items-start gap-3 text-white/90">
                                <img
                                  src={LogoSystemique03}
                                  alt="Systemique - Leergang Duiken in de Onderstroom"
                                  className="mt-0.5 h-4 w-4 object-contain shrink-0"
                                />
                                <span className="leading-relaxed">{item}</span>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 inline-flex items-center justify-center rounded-full bg-white text-[#854C73] px-4 py-2 font-semibold shadow-sm transition duration-200 ease-out hover:shadow-lg hover:scale-[1.02] hover:bg-[#F9EBEF] hover:text-[#6b4912]">
                            Schrijf je in
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    <p className="text-[#6b4912] text-center">
                      Er is plek voor maximaal 8 deelnemers. Vol = vol.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card className="mt-8 border-0 shadow-xl bg-[#854C73] text-white">
            <CardContent className="p-6 md:p-12 text-center">
              <h3
                className="font-serif text-3xl mb-4"
                style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
              >
                Start je leergang systemisch leiderschap
              </h3>
              <p className="text-white/90 mb-8 text-lg font-light leading-relaxed">
                Ben je overtuigd? Schrijf je in voor Duiken in de Onderstroom — de leergang systemisch
                leiderschap van Systemique.
              </p>
              <Button
                size="lg"
                variant="white"
                className="px-8 py-4 text-lg shadow-xl hover:scale-105 transition-transform"
                onClick={() => setLeergangDialogOpen(true)}
              >
                Schrijf je in
              </Button>
            </CardContent>
          </Card>

          <p className="text-sm text-[#6b4912]/60 text-center mt-8 font-light">
            Op deze leergang zijn de{' '}
            <a href={voorwaardenPdf} target="_blank" rel="noreferrer" className="underline text-[#854C73]">
              voorwaarden van Systemique (PDF)
            </a>{' '}
            van toepassing.
          </p>
        </div>
      </section>

      {/* Dialog */}
      <LeergangOnderstroomDialog open={leergangDialogOpen} onOpenChange={setLeergangDialogOpen} />
    </>
  );
};

export default LeergangOnderstroom;
